import type { FC } from 'react'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import {
  CwsButton,
  CwsDivider,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsH1,
  CwsH2,
  CwsH3,
  CwsH4,
  CwsIcon,
  CwsModal,
  CwsModalBody,
  CwsModalFooter,
  CwsP,
} from '@components/cws'
import { useWishlist } from '@hooks/useWishlist'
import { WishlistItem } from '@components/profile/Wishlist/WishlistItem/WishlistItem'
import { useTranslate } from '@hooks/useTranslation'
import { WishlistOptions } from '@components/profile/Wishlist/WishlistOptions/WishlistOptions'
import { useRouter } from 'next/router'
import type { ShareForm } from './SendEmail/SendEmail'
import SendEmail from './SendEmail/SendEmail'
import axios from 'axios'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS } from '@model/locales'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { generateCustAttrsForWishlist } from '@utils/wishlist'
import { getCurrencyOptByLanguageId, useCurrency } from '@hooks/useCurrency'
import type { Catalog } from '@model/pricelist/Catalog'
import { getPrices } from '@utils/product/product-info'
import type { KK_STORE_TYPE } from '@pages/api/proxy'
import { KK_STORE } from '@api/apiConfig'
import { isPromotionValidForCatalog } from '@utils/promotion'

// @ts-ignore
import { modal } from 'cms-web-styleguide/dist/utils/utils'
import { useAlert } from '@context/alert'
import { validations } from '@utils/order'
import type { PlatinumLoungeData } from '@components/profile/Wishlist/PlatinumLoungeContent'
import PlatinumLoungeContent from '@components/profile/Wishlist/PlatinumLoungeContent'
import { useWishListPriceInfos } from '@hooks/useWishListPriceInfos'
import * as R from 'ramda'
import type { Currency, PriceInfo } from '@model/product/PriceInfo'
import { getFormattedPrice } from '@utils/price'
import { useAppState } from '@context/state'
import { useInitialData } from '@context/initialDataContext'

const emailDialogId = 'send-email-form'
const emailSentConfirmId = 'email-sent-confirm'
const confirmOrderDialogId = 'confirm-order-form'
const confirmOrderThankYouDialogId = 'confirm-order-completed'

export type BundleInfo = PriceInfo & { quantity: number; sku: string }
export type BundleGroups = Record<string, BundleInfo[]>

export const freeProductsFactory = (bundleGroups: Record<string, BundleInfo[]>, currency: Currency) => () => {
  let result: Record<string, number | string[]> = {}
  let listTotal = 0
  Object.keys(bundleGroups)
    .filter((key) => (isGloballyPricelist() ? key.includes('bundle') || key.includes('.buy.') : key))
    .forEach((key) => {
      const bundle = bundleGroups[key]
      const priceInfo = bundle[0]
      // const cheapestProduct = R.compose(R.head, R.sortBy(R.prop('normalPrice')))(bundle)
      const allQuantities = R.compose(R.sum, R.map(R.prop('quantity')))(bundle)
      const numberOfBundles = Math.floor(allQuantities / priceInfo.bundleQuantity)
      // const left = allQuantities % priceInfo.bundleQuantity
      const leftOvers = allQuantities - numberOfBundles * priceInfo.bundleQuantity
      // const whatsInBundle = allQuantities - leftOvers
      // const uniqueSkus = R.compose(R.map(R.prop('sku')), R.uniqBy(R.prop('sku')))(bundle)
      const bundledProducts = bundle.sort((a, b) => a.normalPrice - b.normalPrice)
      let accumulator = numberOfBundles
      const prices: Record<string, number> = {}
      bundledProducts.forEach((bi) => {
        listTotal += bi.topPrice * bi.quantity
        prices[bi.sku] = bi.topPrice
        if (numberOfBundles) {
          if (accumulator < 0) return
          const timesFree = accumulator > bi.quantity ? bi.quantity : accumulator
          result[bi.sku] = timesFree
          accumulator -= bi.quantity
          listTotal -= timesFree * bi.topPrice
        }
      })

      const helpingString = Object.keys(result)
        .filter((sku) => result[sku])
        .map((sku) => `${result[sku]} x <strong>${getFormattedPrice(prices[sku], currency)}</strong>`)

      // result.message = `${numberOfBundles} bundles (${numberOfBundles} free products ${helpingString.join(', ')})${
      //   leftOvers ? ` and ${leftOvers} extra` : ''
      // }`

      result.message = [`${numberOfBundles}`, `${helpingString.join(', ')}`, `${leftOvers}`]
      // eslint-disable-next-line no-console
      // console.log(debugMessage)

      // let heap: any[] = []
      // bundle.forEach((bi) => {
      //   for (let i = 0; i < bi.quantity; i++) {
      //     heap.push({ ...bi, quantity: 1 })
      //   }
      // })

      // const bundleProductInRow: BundleInfo[] = [...Array(allQuantities)].reduce((acc, val, idx) => {
      //   const elem = uniqueSkus.length > idx ? idx : allQuantities + (idx % uniqueSkus.length) - allQuantities
      //   const sku = uniqueSkus[elem]
      //   return [...acc, { ...bundle.find((bi) => bi.sku === sku), quantity: 1 }]
      // }, [])
      //
      // result = R.takeLast(leftOvers)(bundleProductInRow)
    })

  return result
}

const Wishlist: FC = () => {
  const { wishlist, sortBy, changeSortBy, wishListId, totalPrice, clearList } = useWishlist()
  const {
    locale,
    query: { catalogId },
  } = useRouter()

  let platinumLounge = false
  if (window && window.envs) {
    platinumLounge = window.envs.isPlatinumLounge
  }

  const languageId = KK_LANG_IDS[locale as Locale]
  const [sendEmailVisible, setSendEmailVisible] = useState(false)
  const [platinumOrderEnabled, setPlatinumOrderEnabled] = useState(false)

  const [shareForm, setShareForm] = useState<ShareForm>({
    email: '',
    message: '',
  })

  const { currency } = useCurrency()

  const bundleInfos = useWishListPriceInfos()
  const bundleGroups = R.groupBy<BundleInfo>(R.prop(isGloballyPricelist() ? 'bundleLabel' : 'bundleType'))(bundleInfos)
  const freeProducts = useMemo(freeProductsFactory(bundleGroups, currency), [bundleGroups, wishlist])

  useEffect(() => {
    setShareForm({
      email: '',
      message: '',
    })
  }, [locale, setShareForm])

  const {
    state: { customer },
  } = useAppState()

  const [platinumLoungeData, setPlatinumLoungeData] = useState<PlatinumLoungeData>({
    name: '',
    deliveryPoint: 'Platinum Lounge',
    clubOne: '',
    message: '',
  })

  useEffect(() => {
    if (customer)
      setPlatinumLoungeData((prevState) => ({
        ...prevState,
        name: `${customer.firstName} ${customer.lastName}`,
        clubOne: customer.extReference2,
      }))
  }, [customer, setPlatinumLoungeData])

  useEffect(() => {
    if (!platinumOrderEnabled)
      setPlatinumLoungeData({
        name: customer ? `${customer.firstName} ${customer.lastName}` : '',
        deliveryPoint: 'Platinum Lounge',
        clubOne: customer?.extReference2 || '',
      })
  }, [platinumOrderEnabled, setPlatinumLoungeData, customer])

  const isEmailFilled = useMemo(
    () =>
      shareForm.copyEmail
        ? shareForm.copyEmail.match(validations.email) && shareForm.email.match(validations.email)
        : shareForm.email.match(validations.email),
    [shareForm.email, shareForm.copyEmail]
  )
  const { translate } = useTranslate()

  const copyright = translate('w2.copyright.message', {
    privacyPolicy: `<a class="cws-a cws-a--theme-white cws-a--size-tiny cws-a--inline cws-a--color-dark-grey" target="_blank" href="https://policies.google.com/privacy?hl=${locale}">${translate(
      'footer.privacy.policy'
    )}</a>`,
    termsOfService: `<a class="cws-a cws-a--theme-white cws-a--size-tiny cws-a--inline cws-a--color-dark-grey" target="_blank" href="https://policies.google.com/terms?hl=${locale}">${translate(
      'w2.terms.of.service'
    )}</a>`,
  })

  const storeId = KK_STORE || isGloballyPricelist() ? 'store1' : 'store2'
  const {
    initialData: { allPromotions, currencies },
  } = useInitialData()
  const currencyOpt = getCurrencyOptByLanguageId(languageId, currencies)
  const filteredPromotions = allPromotions.filter(isPromotionValidForCatalog((catalogId as Catalog) || ''))
  const { showAlert } = useAlert()

  const wlCustomAttrs = useMemo(() => {
    const emailLocale = (locale || 'en') as Locale
    return generateCustAttrsForWishlist({
      catalogId: (catalogId || 'shop') as Catalog,
      currency,
      showAlcoholWarning: false,
      locale: emailLocale,
      catalogTranslation: translate(`route.${catalogId}.lbl`),
      listTotal: totalPrice,
      isPlatinumLoungeOrder: platinumOrderEnabled,
      itemsList: wishlist.map((item) => {
        const priceInfo = getPrices(
          item.product,
          storeId as KK_STORE_TYPE,
          locale as string,
          filteredPromotions,
          currencyOpt,
          '1'
        )
        return {
          ...item,
          product: {
            ...item.product,
            priceInfo,
          },
        }
      }),
      ...(platinumOrderEnabled &&
        platinumLoungeData && {
          platinumOrderName: platinumLoungeData.name,
          platinumOrderCO: platinumLoungeData.clubOne,
          platinumOrderDeliveryPoint: platinumLoungeData.deliveryPoint,
        }),
    })
  }, [
    locale,
    catalogId,
    currency,
    translate,
    totalPrice,
    wishlist,
    storeId,
    filteredPromotions,
    currencyOpt,
    platinumOrderEnabled,
    platinumLoungeData,
  ])

  const sendConfirmOrderEmail = async () => {
    try {
      await axios.post('/api/wishlist/send-email', {
        languageId,
        wishListId,
        catalogId: catalogId || 'shop',
        customAttrs: wlCustomAttrs,
        isPlatinumLoungeOrder: true,
        message: platinumLoungeData?.message || '',
      })
    } catch (e) {
      showAlert({ error: true, text: translate('email.message.error') })
    } finally {
      modal.addEventModalHide(confirmOrderDialogId)
      setPlatinumOrderEnabled(false)
      modal.addEventModalShow(confirmOrderThankYouDialogId)

      try {
        await clearList()
      } catch (e) {}

      setTimeout(() => modal.addEventModalHide(confirmOrderThankYouDialogId), 4000)
    }
  }

  const sendListToEmail = async () => {
    try {
      await axios.post('/api/wishlist/send-email', {
        languageId,
        wishListId,
        catalogId: catalogId || 'shop',
        customAttrs: wlCustomAttrs,
        ...shareForm,
      })
    } catch (e) {
      showAlert({ error: true, text: translate('email.message.error') })
    } finally {
      modal.addEventModalHide(emailDialogId)
      modal.addEventModalShow(emailSentConfirmId)
      setTimeout(() => {
        setShareForm({ email: '', message: '' })
        modal.addEventModalHide(emailSentConfirmId)
      }, 4000)
    }
  }

  const helperMessage = useMemo(() => {
    if (!freeProducts.message) return ''

    try {
      const [numberOfBundles, text, extra = '0'] = freeProducts.message as string[]
      if (parseFloat(numberOfBundles) === 0) return ''
      return translate('bundle.helper.message', { numberOfBundles, text, extra })
    } catch (e) {
      return ''
    }
  }, [translate, freeProducts])

  const [routeName = '', locations = ''] = translate(`route.${catalogId}.lbl`).split(' (')
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)

  return (
    <>
      <CwsGrid width="fluid" className="cws-pb-28 ">
        {isGloballyPricelist() && catalogId ? (
          <>
            <CwsGridRow>
              <CwsGridCol col="12" className="flex items-start">
                <CwsH1>{translate('wishlist.tile.wishlist')}</CwsH1>
              </CwsGridCol>
            </CwsGridRow>
            <CwsGridRow>
              <CwsGridCol col="12" className="flex gap-2 sm:gap-8 cws-mt-20 justify-start">
                <div className="self-center">
                  <CwsP size="small">{translate('w2.pricelist.route.selected')}</CwsP>
                  <CwsH4>{locations.replace(')', '')}</CwsH4>
                </div>
                <div className="h-full max-h-12 self-center">
                  <CwsDivider orientation="vertical" />
                </div>
                <div className="self-center">
                  <CwsP size="small">{translate('w2.pricelist.ship.selected')}</CwsP>
                  <CwsH4>{routeName}</CwsH4>
                </div>
                {platinumLounge && (
                  <div className="self-center ml-auto">
                    <CwsButton
                      disabled={!wishlist.length}
                      onClick={() => {
                        modal.addEventModalShow(confirmOrderDialogId)
                        setPlatinumOrderEnabled(true)
                      }}
                    >
                      {translate('basket.aside.button.place.order')}
                    </CwsButton>
                  </div>
                )}
              </CwsGridCol>
            </CwsGridRow>
          </>
        ) : (
          <CwsGridRow>
            <CwsGridCol col="12" className="flex gap-4 items-center">
              <CwsIcon icon="file-shopping" size="40px" className="mb-5" />
              <CwsH1>{translate('wishlist.share.email.link')}</CwsH1>
            </CwsGridCol>
          </CwsGridRow>
        )}
        <CwsGridRow>
          <CwsGridCol col="12">
            <WishlistOptions setSendEmailVisible={setSendEmailVisible} changeSortBy={changeSortBy} sortBy={sortBy} />
          </CwsGridCol>
          <CwsGridCol col="12">
            <CwsGrid width="fluid" className="hidden lg:block">
              <CwsGridRow noGutter verticalAlign="center">
                <CwsGridCol col="6" className="text-start">
                  <CwsP size="small">{translate('product.details.heading.title')}:</CwsP>
                </CwsGridCol>
                <CwsGridCol col="2" className="text-center">
                  <CwsP size="small">
                    <span className="ml-10">{translate('cart.list.price')}:</span>
                  </CwsP>
                </CwsGridCol>
                <CwsGridCol col="2" className="text-center">
                  <CwsP size="small" color="midnight">
                    <span className="ml-12">{translate('cart.list.quantity')}:</span>
                  </CwsP>
                </CwsGridCol>
                <CwsGridCol col="2" className="text-end">
                  <CwsP size="small" color="midnight">
                    {translate('cart.list.total')}:
                  </CwsP>
                </CwsGridCol>
              </CwsGridRow>
            </CwsGrid>

            {wishlist.map((item, idx) => (
              <Fragment key={item.encodedProduct}>
                <CwsDivider spacing="xs" />
                <WishlistItem
                  item={item}
                  productIndex={idx}
                  bundleGroups={bundleGroups}
                  quantityProductIsFree={(freeProducts[item.product.sku] as number) || 0}
                />
              </Fragment>
            ))}
          </CwsGridCol>
        </CwsGridRow>
        {Boolean(freeProducts.message && (freeProducts.message as string[]).length) && (
          <CwsGridRow>
            <CwsGridCol className="cws-my-28" col="12">
              <CwsP
                className="text-right"
                dangerouslySetInnerHTML={{
                  __html: helperMessage,
                }}
                hideSpacing
                size="small"
                color="dark-grey"
              />
            </CwsGridCol>
          </CwsGridRow>
        )}
      </CwsGrid>
      <CwsModal id={emailDialogId} visible={sendEmailVisible} onClose={() => setSendEmailVisible(false)}>
        <CwsModalBody>
          <CwsH3 className="flex items-center gap-x-4">
            <CwsIcon icon="email" size="30" /> {translate('wishlist.share.email.title')}
          </CwsH3>
          <SendEmail setShareForm={setShareForm} shareForm={shareForm} />
        </CwsModalBody>
        <CwsModalFooter>
          <div className="flex flex-col items-center gap-2 cws-mt-10">
            <CwsButton disabled={!isEmailFilled} onClick={sendListToEmail}>
              {translate('return.form.send')}
            </CwsButton>
            <CwsP className="cws-mt-10" color="dark-grey" dangerouslySetInnerHTML={{ __html: copyright }} size="tiny" />
          </div>
        </CwsModalFooter>
      </CwsModal>
      <CwsModal id={emailSentConfirmId} onClose={() => modal.addEventModalHide(emailSentConfirmId)}>
        <CwsModalBody>
          <CwsH4>{translate('w2.wishlist.shared.heading')}</CwsH4>
          {shareForm.copyEmail ? (
            <CwsP>
              {translate('w2.wishlist.shared.with.bcc', { email: shareForm.email, bccEmail: shareForm.copyEmail })}
            </CwsP>
          ) : (
            <CwsP>{translate('w2.wishlist.shared.info', { email: shareForm.email })}</CwsP>
          )}
        </CwsModalBody>
      </CwsModal>
      {isGloballyPricelist() && platinumLounge && (
        <CwsModal
          id={confirmOrderDialogId}
          onClose={() => {
            modal.addEventModalHide(confirmOrderDialogId)
            setPlatinumOrderEnabled(false)
          }}
        >
          <CwsModalBody>
            <CwsH2>{translate('platinum.lounge.email.title')}</CwsH2>
            <CwsP>{translate('platinum.lounge.email.info')}</CwsP>
            <PlatinumLoungeContent
              data={platinumLoungeData}
              setTermsAccepted={setTermsAccepted}
              setData={setPlatinumLoungeData}
            />
          </CwsModalBody>
          <CwsModalFooter>
            <div className="flex flex-col items-center gap-2">
              <CwsButton disabled={!termsAccepted} onClick={sendConfirmOrderEmail}>
                {translate('platinum.lounge.order.confirm')}
              </CwsButton>
              <CwsP color="dark-grey" dangerouslySetInnerHTML={{ __html: copyright }} size="tiny" />
            </div>
          </CwsModalFooter>
        </CwsModal>
      )}
      {isGloballyPricelist() && platinumLounge && (
        <CwsModal
          id={confirmOrderThankYouDialogId}
          onClose={() => modal.addEventModalHide(confirmOrderThankYouDialogId)}
        >
          <CwsModalBody>
            <CwsH4>{translate('w2.platinum.order.sent.heading')}</CwsH4>
            <CwsP>{translate('w2.platinum.order.sent.info')}</CwsP>
          </CwsModalBody>
        </CwsModal>
      )}
    </>
  )
}
export default Wishlist
